import React from 'react';
import { MagnifyingGlassIcon, PlusIcon, HashtagIcon } from '@heroicons/react/24/outline';

/**
 * Channel list sidebar component
 * @param {Array} channels - Array of channel objects
 * @param {Object} selectedChannel - Currently selected channel
 * @param {Function} onChannelSelect - Handler for channel selection
 * @param {Function} onCreateChannel - Handler for creating new channel
 * @param {string} searchQuery - Search query string
 * @param {Function} onSearchChange - Handler for search input changes
 * @param {boolean} loading - Whether channels are loading
 * @param {Object} userInfo - Current user info object
 */
const ChannelList = ({
  channels,
  selectedChannel,
  onChannelSelect,
  onCreateChannel,
  searchQuery,
  onSearchChange,
  loading,
  userInfo
}) => {
  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (channel.description && channel.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="w-64 bg-white border border-gray-200 flex flex-col shadow-sm rounded-lg overflow-hidden">
      {/* Channel List Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Channels</h2>
          <button
            onClick={onCreateChannel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Create new channel"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search channels..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-white text-gray-700 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Channel List */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-2 py-2">
          {loading && channels.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">Loading channels...</div>
          ) : filteredChannels.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">No channels found</div>
          ) : (
            filteredChannels.map((channel) => (
              <button
                key={channel.id}
                onClick={() => onChannelSelect(channel)}
                className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors flex items-center justify-between group ${
                  selectedChannel?.id === channel.id
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-l-blue-500'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <HashtagIcon className={`w-4 h-4 flex-shrink-0 ${
                    selectedChannel?.id === channel.id ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <span className="text-sm font-medium truncate">{channel.name}</span>
                </div>
                {channel.unread > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full flex-shrink-0">
                    {channel.unread}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* User Info Footer - Fixed at bottom */}
      {userInfo && (
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {userInfo?.Name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-700 truncate">
                {userInfo?.Name || 'User'}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {userInfo?.Status || 'Member'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelList;

